import * as T from "three";
import { Client, getStateCallbacks, type Room } from "colyseus.js";
import { loadRig, PACKS, type PackId, type Rig } from "./character";
import type { ModelAsset } from "./assets";

// Connection to the multiplayer server.
// LAN/Tailscale HTTP pages use ws://hostname:8087.
// Public HTTPS (adventure.keary.co) uses wss://hostname/ws via the reverse proxy.
// Other players are drawn with their chosen character, a name tag, smoothed
// movement, and their car when they're driving. Everything degrades to solo
// play if the server can't be reached.
export const GAME_PORT = 8087;

function gameServerUrl(): string {
  if (location.protocol === "https:") {
    return `wss://${location.host}/ws`;
  }
  return `ws://${location.hostname}:${GAME_PORT}`;
}

type CarLook = { asset: ModelAsset; scale: number; turn: number };
interface Remote {
  root: T.Group;
  rig: Rig | null;
  tag: T.Sprite;
  car: T.Mesh | null;
  carIndex: number;
  target: { x: number; z: number; y: number; rot: number };
  anim: string;
  current: string;
  look: string;
}

function nameTag(text: string) {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 64;
  const g = c.getContext("2d")!;
  g.font = "600 30px system-ui, sans-serif";
  const w = Math.min(248, g.measureText(text).width + 28);
  g.fillStyle = "rgba(20,32,30,0.78)";
  g.beginPath();
  g.roundRect((256 - w) / 2, 10, w, 44, 22);
  g.fill();
  g.fillStyle = "#f6ecd6";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(text, 128, 33, 230);
  const tex = new T.CanvasTexture(c);
  tex.colorSpace = T.SRGBColorSpace;
  const sprite = new T.Sprite(new T.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  sprite.scale.set(2.4, 0.6, 1);
  sprite.renderOrder = 10;
  return sprite;
}

export interface NetEvents {
  chat(from: string, text: string, mine: boolean): void;
  correct(x: number, z: number): void;
  carTaken(index: number, byMe: boolean): void;
  carParked(index: number, x: number, z: number, rot: number): void;
  count(n: number): void;
}

export async function connect(
  scene: T.Scene,
  me: { name: string; pack: PackId; person: string },
  carLook: (index: number) => CarLook | null,
  events: NetEvents,
) {
  const client = new Client(gameServerUrl());
  // Give up after 20 s (slow devices can take a while); if the join completes
  // after that, leave straight away so we aren't a ghost player.
  let gaveUp = false;
  const joining = client.joinOrCreate("city", me);
  joining.then((r) => {
    if (gaveUp) r.leave();
  }).catch(() => {});
  const room: Room = await Promise.race([
    joining,
    new Promise<never>((_, fail) => setTimeout(() => ((gaveUp = true), fail(new Error("timed out joining"))), 20000)),
  ]);
  const $ = getStateCallbacks(room);
  const remotes = new Map<string, Remote>();

  $(room.state).players.onAdd((player: any, id: string) => {
    if (id === room.sessionId) return;
    const root = new T.Group();
    const tag = nameTag(player.name);
    tag.position.y = 2.35;
    root.add(tag);
    root.position.set(player.x, player.y, player.z);
    scene.add(root);
    const r: Remote = { root, rig: null, tag, car: null, carIndex: -1, target: { x: player.x, z: player.z, y: player.y, rot: player.rot }, anim: player.anim, current: "", look: "" };
    remotes.set(id, r);
    const dress = () => {
      const pack = (player.pack in PACKS ? player.pack : "city") as PackId;
      const person = player.person in PACKS[pack].characters ? player.person : Object.keys(PACKS[pack].characters)[0];
      const look = `${pack}/${person}`;
      if (look === r.look) return;
      r.look = look;
      loadRig(pack, person)
        .then((rig) => {
          if (!remotes.has(id) || r.look !== look) return;
          if (r.rig) root.remove(r.rig.model);
          r.rig = rig;
          r.current = "";
          root.add(rig.model);
        })
        .catch(() => {});
    };
    dress();
    $(player).onChange(() => {
      dress();
      r.target = { x: player.x, z: player.z, y: player.y, rot: player.rot };
      r.anim = player.anim;
      if (player.car !== r.carIndex) {
        if (r.car) scene.remove(r.car);
        r.car = null;
        r.carIndex = player.car;
        const look = player.car >= 0 ? carLook(player.car) : null;
        if (look) {
          r.car = new T.Mesh(look.asset.geometry, look.asset.materials);
          r.car.scale.setScalar(look.scale);
          r.car.userData.turn = look.turn;
          r.car.castShadow = true;
          scene.add(r.car);
        }
      }
    });
    events.count(room.state.players.size);
  });
  $(room.state).players.onRemove((_: any, id: string) => {
    const r = remotes.get(id);
    if (r) {
      scene.remove(r.root);
      if (r.car) scene.remove(r.car);
      remotes.delete(id);
    }
    events.count(room.state.players.size);
  });
  // Cars other people take and park.
  $(room.state).cars.onAdd((car: any, key: string) => {
    const index = Number(key);
    const apply = () => {
      if (car.driver) events.carTaken(index, car.driver === room.sessionId);
      else events.carParked(index, car.x, car.z, car.rot);
    };
    apply();
    $(car).listen("driver", apply);
  });

  room.onMessage("chat", (m: { from: string; id: string; text: string }) => events.chat(m.from, m.text, m.id === room.sessionId));
  room.onMessage("correct", (m: { x: number; z: number }) => events.correct(m.x, m.z));
  const pendingTakes = new Map<number, (ok: boolean) => void>();
  room.onMessage("taken", (m: { index: number; ok: boolean }) => {
    pendingTakes.get(m.index)?.(m.ok);
    pendingTakes.delete(m.index);
  });
  room.onLeave(() => events.count(0));

  let lastSent = 0,
    lastKey = "";
  // Wait (briefly) for our own record so we can start where the server put us.
  for (let i = 0; i < 50 && !room.state?.players?.get(room.sessionId); i++) await new Promise((r) => setTimeout(r, 40));
  const mine = room.state?.players?.get(room.sessionId);
  return {
    /** Where the server placed us on joining (spread out so nobody overlaps). */
    spawn: mine ? { x: mine.x as number, z: mine.z as number } : null,
    get online() {
      return room.connection.isOpen;
    },
    /** Send our position ~12 times a second (only when something changed). */
    send(now: number, s: { x: number; z: number; y: number; rot: number; anim: string }) {
      if (now - lastSent < 80) return;
      const key = `${s.x.toFixed(2)},${s.z.toFixed(2)},${s.y.toFixed(2)},${s.rot.toFixed(2)},${s.anim}`;
      if (key === lastKey && now - lastSent < 1000) return;
      lastSent = now;
      lastKey = key;
      room.send("move", s);
    },
    take(index: number) {
      return new Promise<boolean>((resolve) => {
        pendingTakes.set(index, resolve);
        room.send("take", { index });
        setTimeout(() => pendingTakes.has(index) && (pendingTakes.delete(index), resolve(false)), 3000);
      });
    },
    park() {
      room.send("park");
    },
    look(pack: PackId, person: string) {
      room.send("look", { pack, person });
    },
    chat(text: string) {
      room.send("chat", { text });
    },
    /** Smooth other players towards their latest positions and animate them. */
    update(dt: number) {
      const k = Math.min(1, dt * 10);
      for (const r of remotes.values()) {
        const p = r.root.position;
        p.x += (r.target.x - p.x) * k;
        p.z += (r.target.z - p.z) * k;
        p.y += (r.target.y - p.y) * k;
        const turn = T.MathUtils.euclideanModulo(r.target.rot - r.root.rotation.y + Math.PI, Math.PI * 2) - Math.PI;
        r.root.rotation.y += turn * k;
        const driving = !!r.car;
        if (r.rig) r.rig.model.visible = !driving;
        r.tag.position.y = driving ? 2.6 : 2.35;
        if (r.car) {
          r.car.position.set(p.x, -0.15, p.z);
          r.car.rotation.y = r.root.rotation.y + (r.car.userData.turn ?? 0);
        }
        if (r.rig && r.anim !== r.current) {
          const move = r.anim === "drive" ? "idle" : r.anim === "jump" ? "jumpAir" : r.anim;
          const next = r.rig.actions[move as keyof Rig["actions"]] ?? r.rig.actions.idle;
          for (const list of Object.values(r.rig.actions)) for (const a of list ?? []) if (!next?.includes(a)) a.fadeOut(0.2);
          for (const a of next ?? []) a.reset().fadeIn(0.2).play();
          r.current = r.anim;
        }
        if (r.rig) for (const m of r.rig.mixers) m.update(dt);
      }
    },
    leave() {
      room.leave();
    },
  };
}
